/**
 * iter133 — Shared "Filter Places" sheet used across:
 *   • Discover (Home)
 *   • Tonight
 *   • Map (overlay)
 *   • My Favorites
 *   • My Saved
 *
 * Self-contained bottom sheet (does NOT wrap the legacy FilterSheet).
 * Sections:
 *   1. Category multi-select pills — dynamic from the `businesses` prop;
 *      only renders categories that have at least one business present.
 *   2. Toggles — Open Now / VIP Only / Cigar Friendly.
 *   3. Distance pills — Nearby (<1km) / Walking (1-3km) / Any.
 *   4. Minimum Rating pills — 4+ / 3+ / Any.
 *   5. Sort By pills — Nearest / Highest Rated / Most Popular / Recently Added.
 *
 * Footer: "Show X results" CTA + "Clear all" ghost.
 *
 * Also exports `applyFilters(businesses, filters, {coords})` — the single
 * source of truth for filtering logic across every consumer.
 */
import { useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { Switch } from "./ui/switch";
import { CATEGORY_CHIPS, haversineKm } from "../lib/geo";
import { useI18n } from "../lib/i18n";
import { getL2ForParent } from "../lib/categoryHierarchy";

export const DEFAULT_FILTERS = {
  categories: [],       // array of Level-1 category slugs (multi-select)
  subcategories: [],    // iter154 — array of Level-2 sub-category slugs
  openNow: false,
  vipOnly: false,
  cigarFriendly: false,
  distance: "any",      // "near" | "walking" | "any"
  minRating: "any",     // "4plus" | "3plus" | "any"
  sortBy: "nearest",    // "nearest" | "rating" | "popular" | "recent"
};

export function isDefaultFilters(f) {
  if (!f) return true;
  return (
    (f.categories?.length ?? 0) === 0 &&
    (f.subcategories?.length ?? 0) === 0 &&
    !f.openNow && !f.vipOnly && !f.cigarFriendly &&
    f.distance === "any" && f.minRating === "any" && f.sortBy === "nearest"
  );
}

export function countActiveFilters(f) {
  if (!f) return 0;
  let n = 0;
  if (f.categories?.length) n += f.categories.length;
  if (f.subcategories?.length) n += f.subcategories.length;
  if (f.openNow) n++;
  if (f.vipOnly) n++;
  if (f.cigarFriendly) n++;
  if (f.distance !== "any") n++;
  if (f.minRating !== "any") n++;
  if (f.sortBy !== "nearest") n++;
  return n;
}

/**
 * Central filter helper. Returns the filtered + sorted business list.
 * `coords` optional — required for distance-based filters/sort.
 */
export function applyFilters(items, filters, { coords } = {}) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const f = filters || DEFAULT_FILTERS;

  const distOf = (b) => (coords && b?.lat != null && b?.lng != null)
    ? haversineKm(coords, { lat: b.lat, lng: b.lng })
    : null;

  let rows = items.slice();

  if (f.categories?.length) {
    const set = new Set(f.categories);
    rows = rows.filter((b) => set.has(b.category));
  }
  if (f.subcategories?.length) {
    const set = new Set(f.subcategories);
    rows = rows.filter((b) => b.subcategory && set.has(b.subcategory));
  }
  if (f.openNow) rows = rows.filter((b) => b.open_now === true || b.here_tonight === true);
  if (f.vipOnly) rows = rows.filter((b) => b.vip_enabled === true);
  if (f.cigarFriendly) {
    rows = rows.filter((b) =>
      b.smoking_allowed === true
      || b.smoking_policy === "cigar_friendly"
      || b.smoking_policy === "smoking"
      || ["cigar_lounge", "shop"].includes(b.category),
    );
  }
  if (f.distance !== "any") {
    const max = f.distance === "near" ? 1 : 3;
    rows = rows.filter((b) => {
      const d = distOf(b);
      return d != null && d <= max;
    });
  }
  if (f.minRating !== "any") {
    const min = f.minRating === "4plus" ? 4 : 3;
    rows = rows.filter((b) => (b.ratings_average || b.rating_avg || 0) >= min);
  }

  const cmp = {
    nearest: (a, b) => {
      const da = distOf(a); const db = distOf(b);
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    },
    rating:  (a, b) => (b.ratings_average || b.rating_avg || 0) - (a.ratings_average || a.rating_avg || 0),
    popular: (a, b) => (b.ratings_count || b.approved_review_count || 0) - (a.ratings_count || a.approved_review_count || 0),
    recent:  (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")),
  }[f.sortBy] || (() => 0);
  rows.sort(cmp);
  return rows;
}

const CHIP_BASE = {
  minHeight: 40,
  padding: "8px 14px",
  borderRadius: 9999,
  fontSize: 12,
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 500,
  transition: "background-color 200ms, color 200ms, border-color 200ms",
  cursor: "pointer",
};

function CategoryPills({ businesses, value, subValue, onToggleL1, onToggleSub, t, lang }) {
  // Build the set of categories that ACTUALLY appear in the current
  // businesses array. Order them following CATEGORY_CHIPS canonical order
  // so the row stays stable as the user toggles other filters.
  const present = useMemo(() => {
    const set = new Set();
    for (const b of businesses || []) if (b?.category) set.add(b.category);
    return CATEGORY_CHIPS
      .filter((c) => c.key !== "all" && set.has(c.key))
      .map((c) => c);
  }, [businesses]);

  // iter154 — For every SELECTED L1, look up its Level-2 sub-categories
  // from the hierarchy cache and filter to those that appear in the
  // current business list (never render an empty sub-pill).
  const subsBySlug = useMemo(() => {
    if (!value?.length) return {};
    const bizSubBySub = new Map();
    for (const b of businesses || []) {
      if (b?.subcategory && b?.category) {
        const key = `${b.category}::${b.subcategory}`;
        bizSubBySub.set(key, (bizSubBySub.get(key) || 0) + 1);
      }
    }
    const out = {};
    for (const l1 of value) {
      const l2s = getL2ForParent(l1);
      const usable = l2s.filter((s) => bizSubBySub.has(`${l1}::${s.slug}`));
      if (usable.length) out[l1] = usable;
    }
    return out;
  }, [businesses, value]);

  if (present.length === 0) return null;

  const toggle = (key) => onToggleL1(key);
  const toggleSub = (subSlug) => onToggleSub(subSlug);

  return (
    <div className="flex flex-col gap-3" data-testid="filter-categories-pills">
      <div className="flex gap-2 flex-wrap">
        {present.map((c) => {
          const active = value.includes(c.key);
          return (
            <button
              key={c.key}
              type="button"
              data-testid={`filter-category-${c.key}`}
              aria-pressed={active}
              onClick={() => toggle(c.key)}
              style={{
                ...CHIP_BASE,
                background: active ? "#E8B84B" : "transparent",
                color: active ? "#0B0B0B" : "#E8B84B",
                border: `1px solid ${active ? "#E8B84B" : "rgba(232,184,75,0.4)"}`,
              }}
            >
              {t(c.tKey) || c.label}
            </button>
          );
        })}
      </div>

      {/* iter154 — Indented Level-2 pills, grouped by parent. Only rendered
          when the corresponding L1 is selected AND has usable sub-cats. */}
      {value.map((l1) => {
        const subs = subsBySlug[l1];
        if (!subs?.length) return null;
        return (
          <div
            key={`subs-${l1}`}
            className="pl-4 flex gap-2 flex-wrap"
            style={{ borderLeft: "2px solid rgba(232,184,75,0.25)" }}
            data-testid={`filter-subcategory-group-${l1}`}
          >
            {subs.map((s) => {
              const active = subValue.includes(s.slug);
              const label = (lang === "es" ? s.name_es : s.name_en) || s.slug;
              return (
                <button
                  key={s.slug}
                  type="button"
                  data-testid={`filter-subcategory-${l1}-${s.slug}`}
                  aria-pressed={active}
                  onClick={() => toggleSub(s.slug)}
                  style={{
                    ...CHIP_BASE,
                    fontSize: 11,
                    minHeight: 34,
                    padding: "6px 12px",
                    background: active ? "#E8B84B" : "transparent",
                    color: active ? "#0B0B0B" : "rgba(232,184,75,0.85)",
                    border: `1px solid ${active ? "#E8B84B" : "rgba(232,184,75,0.3)"}`,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function PillRow({ value, onChange, options, testidPrefix }) {
  return (
    <div className="flex gap-2 flex-wrap" role="radiogroup">
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`${testidPrefix}-${o.key}`}
            onClick={() => onChange(o.key)}
            style={{
              ...CHIP_BASE,
              background: active ? "#E8B84B" : "transparent",
              color: active ? "#0B0B0B" : "#E8B84B",
              border: `1px solid ${active ? "#E8B84B" : "rgba(232,184,75,0.4)"}`,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ToggleRow({ label, sub, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4" style={{ minHeight: 48 }}>
      <div className="flex-1 min-w-0">
        <div className="font-display text-foreground text-base">{label}</div>
        <div className="text-[14px] text-foreground/55 mt-0.5">{sub}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

export default function FilterPlacesSheet({
  isOpen,
  onClose,
  onApply,
  currentFilters,
  businesses,
  resultCount,
}) {
  const { t, lang } = useI18n();
  const filters = currentFilters || DEFAULT_FILTERS;

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const update = (patch) => onApply?.({ ...filters, ...patch });
  const clearAll = () => onApply?.(DEFAULT_FILTERS);

  return (
    <>
      <button
        type="button"
        aria-label="Dismiss filters"
        data-testid="filter-places-scrim"
        onClick={onClose}
        className="fixed inset-0 z-[80]"
        style={{ background: "rgba(0,0,0,0.72)" }}
      />
      <div
        data-testid="filter-places-sheet"
        className="fixed left-0 right-0 bottom-0 z-[81]"
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.3)",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* iter134 — Non-scrolling header (handle + title). X button lives
            inside this header at position:absolute so it stays pinned to
            the top-right of the sheet and never scrolls away. */}
        <div style={{ position: "relative", flex: "0 0 auto" }}>
          <div className="grid place-items-center pt-3 pb-2">
            <span style={{ width: 44, height: 4, borderRadius: 9999, background: "rgba(232,184,75,0.45)" }} />
          </div>
          <div className="px-6 pt-2 pb-4">
            <h2 className="font-display text-2xl text-foreground pr-12" data-testid="filter-places-title">
              {t("filter_places_title") || "Filter Places"}
            </h2>
          </div>
          <button
            type="button"
            data-testid="filter-places-close"
            onClick={onClose}
            aria-label="Close"
            className="grid place-items-center"
            style={{
              position: "absolute",
              top: 12,
              right: 14,
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#222",
              color: "#fff",
              border: "none",
              zIndex: 90,
              boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
            }}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>

        <div className="px-6 space-y-7">
          {/* Categories (dynamic multi-select) */}
          {businesses?.length > 0 && (
            <div>
              <div className="eyebrow mb-3" data-testid="filter-section-categories">
                {t("filter_section_categories") || "Categories"}
              </div>
              <CategoryPills
                businesses={businesses}
                value={filters.categories || []}
                subValue={filters.subcategories || []}
                onToggleL1={(key) => {
                  const currCats = filters.categories || [];
                  const nextCats = currCats.includes(key)
                    ? currCats.filter((k) => k !== key)
                    : [...currCats, key];
                  const currSubs = filters.subcategories || [];
                  const nextSubs = currSubs.filter((sub) => {
                    for (const l1 of nextCats) {
                      if (getL2ForParent(l1).some((s) => s.slug === sub)) return true;
                    }
                    return false;
                  });
                  update({ categories: nextCats, subcategories: nextSubs });
                }}
                onToggleSub={(subSlug) => {
                  const curr = filters.subcategories || [];
                  const next = curr.includes(subSlug)
                    ? curr.filter((k) => k !== subSlug)
                    : [...curr, subSlug];
                  update({ subcategories: next });
                }}
                t={t}
                lang={lang}
              />
            </div>
          )}

          {/* Toggles */}
          <div className="space-y-4">
            <ToggleRow
              label={t("filter_open_now") || "Open Now"}
              sub={t("filter_open_now_sub") || "Show only places currently open"}
              checked={filters.openNow}
              onChange={(v) => update({ openNow: v, sortBy: v ? "nearest" : filters.sortBy })}
              testid="filter-open-now"
            />
            <ToggleRow
              label={t("filter_vip_only") || "VIP Only"}
              sub={t("filter_vip_only_sub") || "Show only VIP-enabled venues"}
              checked={filters.vipOnly}
              onChange={(v) => update({ vipOnly: v })}
              testid="filter-vip-only"
            />
            <ToggleRow
              label={t("filter_cigar_friendly") || "Cigar Friendly"}
              sub={t("filter_cigar_friendly_sub") || "Show only smoking-allowed venues"}
              checked={filters.cigarFriendly}
              onChange={(v) => update({ cigarFriendly: v })}
              testid="filter-cigar-friendly"
            />
          </div>

          {/* Distance */}
          <div>
            <div className="eyebrow mb-3">{t("filter_distance") || "Distance"}</div>
            <PillRow
              testidPrefix="filter-distance"
              value={filters.distance}
              onChange={(v) => update({ distance: v })}
              options={[
                { key: "near", label: t("filter_distance_near") || "Nearby (<1 km)" },
                { key: "walking", label: t("filter_distance_walking") || "Walking (1–3 km)" },
                { key: "any", label: t("filter_any") || "Any" },
              ]}
            />
          </div>

          {/* Min Rating */}
          <div>
            <div className="eyebrow mb-3">{t("filter_min_rating") || "Minimum Rating"}</div>
            <PillRow
              testidPrefix="filter-rating"
              value={filters.minRating}
              onChange={(v) => update({ minRating: v, sortBy: v !== "any" ? "rating" : filters.sortBy })}
              options={[
                { key: "4plus", label: t("filter_rating_4plus") || "4+ Stars" },
                { key: "3plus", label: t("filter_rating_3plus") || "3+ Stars" },
                { key: "any", label: t("filter_any") || "Any" },
              ]}
            />
          </div>

          {/* Sort By */}
          <div>
            <div className="eyebrow mb-3">{t("filter_sort_by") || "Sort By"}</div>
            <PillRow
              testidPrefix="filter-sort"
              value={filters.sortBy}
              onChange={(v) => update({ sortBy: v })}
              options={[
                { key: "nearest", label: t("filter_sort_nearest") || "Nearest" },
                { key: "rating", label: t("filter_sort_rating") || "Highest Rated" },
                { key: "popular", label: t("filter_sort_popular") || "Most Popular" },
                { key: "recent", label: t("filter_sort_recent") || "Recently Added" },
              ]}
            />
          </div>
        </div>

        {/* iter134 — Auto-apply on every selection; no "Show X results" button.
            Footer keeps only "Clear all" on the left. */}
        <div className="mt-7 px-6 pt-4 pb-2 flex items-center justify-between gap-3" style={{ borderTop: "1px solid rgba(232,184,75,0.15)" }}>
          <button
            type="button"
            data-testid="filter-places-clear"
            onClick={clearAll}
            className="text-[14px] text-foreground/65 hover:text-foreground transition-colors"
            style={{ minHeight: 48, fontFamily: "'DM Sans', sans-serif" }}
          >
            {t("filter_clear_all") || "Clear all"}
          </button>
          <div
            data-testid="filter-places-live-count"
            className="text-[14px] text-gold"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.04em" }}
          >
            {(t("filter_show_results") || "Show {n} results").replace("{n}", resultCount ?? 0)}
          </div>
        </div>
        </div>{/* /scrollable */}
      </div>
    </>
  );
}
